import type { ModelCamera, ModelPoint, ModelViewLimits } from '../../types/crag-route';

/**
 * The arithmetic of a sector's limits on its model (ModelViewLimits). Pure, with
 * nothing of three.js in it: the panel's sliders use it too, and those are not in the
 * lazy chunk. See docs/CRAG_3D_SECTOR_MODEL_PLAN.md.
 */

/** Matches ParsesRouteLine's MAX_MODEL_LIMIT_* and MIN_MODEL_LIMIT_DISTANCE. */
export const MODEL_LIMIT_BOUNDS = {
  degrees: 180,
  minDistance: 0.1,
  maxDistance: 10,
  maxPan: 2,
} as const;

/**
 * Where the sliders start: wide enough to be tried out and narrowed from there. The
 * slide is the small one, because it adds to the rest: at 50% on the first real model,
 * a camera close in and turned could end up looking at no rock at all.
 */
export const DEFAULT_MODEL_LIMITS: ModelViewLimits = {
  azimuth: [-90, 90],
  polar: [-45, 30],
  distance: [0.3, 2],
  pan: 0.25,
};

/** The limits in the terms of the orbit: radians, and lengths in the model's scale. */
export interface ResolvedModelLimits {
  /** The view's azimuth, which `azimuth` is measured from. */
  baseAzimuth: number;
  /** Radians either side of `baseAzimuth`. Null when it may go all the way round. */
  azimuth: [number, number] | null;
  /** Absolute polar angles: 0 looks straight down from above. */
  polar: [number, number];
  distance: [number, number];
  panRadius: number;
  centre: ModelPoint;
}

const TURN = Math.PI * 2;

function toRadians(degrees: number) {
  return (degrees * Math.PI) / 180;
}

/** Into -π…π. */
function wrapAngle(angle: number) {
  return ((angle + Math.PI) % TURN + TURN) % TURN - Math.PI;
}

/** As three's Spherical: azimuth from +z towards +x, polar from +y. */
function spherical({ position, target }: ModelCamera) {
  const x = position.x - target.x;
  const y = position.y - target.y;
  const z = position.z - target.z;
  const radius = Math.hypot(x, y, z);
  return {
    azimuth: Math.atan2(x, z),
    polar: radius > 0 ? Math.acos(Math.min(Math.max(y / radius, -1), 1)) : Math.PI / 2,
    radius,
  };
}

function placeCamera(target: ModelPoint, azimuth: number, polar: number, radius: number): ModelCamera {
  const sinPolar = Math.sin(polar);
  return {
    position: {
      x: target.x + radius * sinPolar * Math.sin(azimuth),
      y: target.y + radius * Math.cos(polar),
      z: target.z + radius * sinPolar * Math.cos(azimuth),
    },
    target,
  };
}

/** The polar angle of a view in degrees: what the vertical slider's ends are worked out from. */
export function viewPolarDegrees(view: ModelCamera) {
  return (spherical(view).polar * 180) / Math.PI;
}

/** The limits as they apply to this view. */
export function resolveLimits(view: ModelCamera, limits: ModelViewLimits): ResolvedModelLimits {
  const base = spherical(view);
  const radius = base.radius || 1;
  const [left, right] = limits.azimuth;

  return {
    baseAzimuth: base.azimuth,
    azimuth: right - left >= 360 ? null : [toRadians(left), toRadians(right)],
    polar: [
      Math.max(base.polar + toRadians(limits.polar[0]), 0),
      Math.min(base.polar + toRadians(limits.polar[1]), Math.PI),
    ],
    distance: [limits.distance[0] * radius, limits.distance[1] * radius],
    panRadius: limits.pan * radius,
    centre: view.target,
  };
}

/**
 * The nearest camera the limits allow, looking at the same point: where a flight
 * lands, so that it never has to be pushed back once there.
 */
export function clampCamera(camera: ModelCamera, limits: ResolvedModelLimits): ModelCamera {
  const { azimuth, polar, radius } = spherical(camera);

  let offset = wrapAngle(azimuth - limits.baseAzimuth);
  if (limits.azimuth) {
    const [low, high] = limits.azimuth;
    if (offset < low || offset > high) {
      // The way round that is shorter: past the right limit may be closer to the left.
      const toLow = wrapAngle(low - offset);
      const toHigh = wrapAngle(high - offset);
      offset = Math.abs(toLow) < Math.abs(toHigh) ? low : high;
    }
  }

  return placeCamera(
    camera.target,
    limits.baseAzimuth + offset,
    Math.min(Math.max(polar, limits.polar[0]), limits.polar[1]),
    Math.min(Math.max(radius, limits.distance[0]), limits.distance[1]),
  );
}

export interface ModelViewChange {
  azimuth?: number;
  polar?: number;
  distance?: number;
  pan?: number;
}

/** The view turned, moved away or slid by this much: what a slider's thumb is showing. */
export function cameraAt(view: ModelCamera, { azimuth = 0, polar = 0, distance = 1, pan = 0 }: ModelViewChange): ModelCamera {
  const base = spherical(view);
  const radius = base.radius || 1;
  const placed = placeCamera(
    view.target,
    base.azimuth + toRadians(azimuth),
    Math.min(Math.max(base.polar + toRadians(polar), 0.001), Math.PI - 0.001),
    radius * distance,
  );
  if (pan === 0) return placed;

  // To the camera's right, level: the slide you would see as the model moving left.
  const shift = {
    x: Math.cos(base.azimuth) * pan * radius,
    y: 0,
    z: -Math.sin(base.azimuth) * pan * radius,
  };
  const moved = (point: ModelPoint) => ({ x: point.x + shift.x, y: point.y + shift.y, z: point.z + shift.z });
  return { position: moved(placed.position), target: moved(placed.target) };
}

/**
 * The distance slider's scale: logarithmic, so 10% and 1000% sit the same way either
 * side of 100%, which is in the middle of a 0-100 track.
 */
export function distanceToSlider(share: number) {
  return 50 + 50 * Math.log10(share);
}

export function sliderToDistance(value: number) {
  return 10 ** ((value - 50) / 50);
}
